/**
 * Channel catalog — the SINGLE SOURCE OF TRUTH for every communication channel
 * in the Brokly platform (Story V10-A Zentrale Kanalsteuerung, V10 Kap. 21).
 *
 * DESIGN DECISIONS baked in:
 *
 * 1. CATALOG = CODE, NOT A TABLE. The DB stores only per-tenant channel *config*
 *    (channel_config rows). The catalog itself (keys + kind flags) is never
 *    persisted as a source of truth; kind flags are seeded from here and are
 *    NOT updatable via the API (catalog-fixed).
 *
 * 2. Kind flags per V10 Kap. 21.1:
 *    Eingang:         email, portal, peax, epost, upload, scan, browser_extension
 *    Zustellung:      portal, email, briefpost, peax, epost
 *    Benachrichtigung: email, sms, push
 *    (portal/peax/epost are beidseitig — Eingang + Zustellung; email is all three.)
 *
 * 3. "No magic values" — import these constants, never inline the strings.
 *    [Source: docs/architecture/coding-standards.md §1]
 *
 * 4. `as const satisfies` keeps each key as a narrow string-literal type while
 *    TypeScript still checks the shape (mirrors permission.ts).
 *
 * [Source: docs/concept/2026-07-02_stefan-concept_V10.txt Kap. 21.1–21.5]
 * [Source: docs/stories/v10-a-kanalsteuerung.md AC2]
 * [Source: packages/shared/src/permission.ts] (catalog-as-code style template)
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/** Shape of a single channel definition in the static catalog. */
export interface ChannelDefinition {
  key: string;
  label: string;
  eingang: boolean;
  zustellung: boolean;
  benachrichtigung: boolean;
}

// ── Channel catalog (Kap. 21.1) ───────────────────────────────────────────────

/**
 * The static channel catalog: 10 channels with their kind flags.
 * Kind flags are catalog-fixed — never updatable via DTO/API.
 */
export const CHANNEL_CATALOG = [
  { key: 'portal',            label: 'Kundenportal',       eingang: true,  zustellung: true,  benachrichtigung: false },
  { key: 'email',             label: 'E-Mail',             eingang: true,  zustellung: true,  benachrichtigung: true  },
  { key: 'briefpost',         label: 'Briefpost',          eingang: false, zustellung: true,  benachrichtigung: false },
  { key: 'peax',              label: 'PEAX',               eingang: true,  zustellung: true,  benachrichtigung: false },
  { key: 'epost',             label: 'E-Post',             eingang: true,  zustellung: true,  benachrichtigung: false },
  { key: 'upload',            label: 'Upload',             eingang: true,  zustellung: false, benachrichtigung: false },
  { key: 'scan',              label: 'Scan',               eingang: true,  zustellung: false, benachrichtigung: false },
  { key: 'browser_extension', label: 'Browser-Extension',  eingang: true,  zustellung: false, benachrichtigung: false },
  { key: 'sms',               label: 'SMS',                eingang: false, zustellung: false, benachrichtigung: true  },
  { key: 'push',              label: 'Push',               eingang: false, zustellung: false, benachrichtigung: true  },
] as const satisfies readonly ChannelDefinition[];

/** Union type of every valid channel key (narrow string literals). */
export type ChannelKey = (typeof CHANNEL_CATALOG)[number]['key'];

/** Flat readonly array of every channel key (in catalog order). */
export const CHANNEL_KEYS = CHANNEL_CATALOG.map((c) => c.key) as readonly ChannelKey[];

/** Zustellung-capable channel keys (catalog order) — the only valid delivery-order members. */
export const ZUSTELLUNG_CHANNEL_KEYS = CHANNEL_CATALOG
  .filter((c) => c.zustellung)
  .map((c) => c.key) as readonly ChannelKey[];

/** Benachrichtigung-capable channel keys (catalog order). */
export const BENACHRICHTIGUNG_CHANNEL_KEYS = CHANNEL_CATALOG
  .filter((c) => c.benachrichtigung)
  .map((c) => c.key) as readonly ChannelKey[];

/**
 * Versand-capable channel keys: zustellung-capable AND active by default, so the api never
 * accepts a channel no client shows. Narrower than ZUSTELLUNG_CHANNEL_KEYS (peax/epost are
 * zustellung but inactive, no adapter yet).
 */
export const VERSAND_CHANNEL_KEYS = ['portal', 'email', 'briefpost'] as const satisfies readonly ChannelKey[];

/** Union type of the notification-capable channel keys ('email' | 'sms' | 'push'). */
export type NotificationChannelKey = 'email' | 'sms' | 'push';

// ── Delivery doc types (Kap. 21.4) ────────────────────────────────────────────

/**
 * The document types the Kanal-Zuordnungsmatrix steers (Kap. 21.4):
 * per Dokumenttyp × Produkt/Sparte (nullable) × Standardkanal.
 */
export const DELIVERY_DOC_TYPES = [
  'mandat',
  'vertrag',
  'rechnung',
  'mahnung',
  'korrespondenz',
] as const;

/** Union type of every valid delivery doc type. */
export type DeliveryDocType = (typeof DELIVERY_DOC_TYPES)[number];

/** Named-constant map for delivery doc types (no magic strings at call sites). */
export const DELIVERY_DOC_TYPE = {
  MANDAT:        'mandat',
  VERTRAG:       'vertrag',
  RECHNUNG:      'rechnung',
  MAHNUNG:       'mahnung',
  KORRESPONDENZ: 'korrespondenz',
} as const satisfies Record<string, DeliveryDocType>;

// ── Default Zustellreihenfolge (Kap. 21.3) ────────────────────────────────────

/**
 * Global default delivery order: 1. Kundenportal → 2. E-Mail → 3. Briefpost.
 * Used as the priority tie-breaker in ChannelResolutionService.
 * [Source: docs/concept/2026-07-02_stefan-concept_V10.txt Kap. 21.3]
 */
export const DEFAULT_ZUSTELL_ORDER = ['portal', 'email', 'briefpost'] as const satisfies readonly ChannelKey[];

// ── Default per-channel configs (seed AND lazy-default source — ONE constant) ─

/** Shape of a single default channel config entry. */
export interface DefaultChannelConfig {
  active: boolean;
  priority: number | null;
  fallbackChannel: ChannelKey | null;
}

/**
 * DEFAULT_CHANNEL_CONFIGS — the in-code default config per channel.
 *
 * Used by BOTH the boot seed (ChannelConfigSeedService) and the lazy default
 * (reads/resolution fall back here for any missing row) — ONE constant, no drift.
 *
 * Rationale: default global order portal→email→briefpost = Kap. 21.3.
 * PEAX / E-Post / SMS / Push have no adapters yet → inactive by default
 * (interface-first; activating them is an admin toggle once adapters exist).
 * sender/recipient/retention_note/manage_permission default to null.
 *
 * [Source: docs/stories/v10-a-kanalsteuerung.md Dev Notes "Default channel configs"]
 */
export const DEFAULT_CHANNEL_CONFIGS: Readonly<Record<ChannelKey, DefaultChannelConfig>> = {
  portal:            { active: true,  priority: 1,    fallbackChannel: 'email' },
  email:             { active: true,  priority: 2,    fallbackChannel: 'briefpost' },
  briefpost:         { active: true,  priority: 3,    fallbackChannel: null },
  peax:              { active: false, priority: 4,    fallbackChannel: 'email' },
  epost:             { active: false, priority: 5,    fallbackChannel: 'email' },
  upload:            { active: true,  priority: null, fallbackChannel: null },
  scan:              { active: true,  priority: null, fallbackChannel: null },
  browser_extension: { active: true,  priority: null, fallbackChannel: null },
  sms:               { active: false, priority: 2,    fallbackChannel: 'email' },
  push:              { active: false, priority: 3,    fallbackChannel: 'email' },
} as const;

// ── Channel-key → legacy delivery_channel mapping ─────────────────────────────

/**
 * Maps a resolved channel key onto the legacy `delivery_channel` PG enum
 * ('email' | 'post' | 'portal') used by the mandate dispatch path.
 *
 * Channels WITHOUT a mapping (peax/epost/...) have no legacy dispatch equivalent —
 * the dispatch re-point falls to the resolved channel's configured fallback,
 * else 'email' (total function at the consumer).
 *
 * [Source: packages/db/src/schema/_enums.ts (delivery_channel)]
 * [Source: docs/stories/v10-a-kanalsteuerung.md AC8]
 */
export const CHANNEL_TO_DELIVERY_CHANNEL: Readonly<Partial<Record<ChannelKey, 'email' | 'post' | 'portal'>>> = {
  portal:    'portal',
  email:     'email',
  briefpost: 'post',
} as const;

// ── Resolution source ─────────────────────────────────────────────────────────

/**
 * CHANNEL_SOURCE — how a delivery channel was resolved (precedence step a–d,
 * plus 'explicit' when a caller supplied the channel directly, and 'fallback' when no
 * configuration chose it and the hard-coded email default was used).
 */
export const CHANNEL_SOURCE = {
  PORTAL_ACCESS:  'portal_access',
  CUSTOMER_ORDER: 'customer_order',
  MATRIX:         'matrix',
  GLOBAL_ORDER:   'global_order',
  EXPLICIT:       'explicit',
  FALLBACK:       'fallback',
} as const;

/** Union type of every valid channel-resolution source. */
export type ChannelSource = (typeof CHANNEL_SOURCE)[keyof typeof CHANNEL_SOURCE];

// ── Wire types (api ⇄ web) ────────────────────────────────────────────────────

/**
 * ChannelConfigDto — one channel's effective config for the tenant.
 * Merged stored-row-over-default; is_default marks a channel with no stored row.
 * Kind flags come from the catalog (never updatable).
 */
export interface ChannelConfigDto {
  channel_key: ChannelKey;
  label: string;
  is_eingang: boolean;
  is_zustellung: boolean;
  is_benachrichtigung: boolean;
  active: boolean;
  priority: number | null;
  fallback_channel: ChannelKey | null;
  default_sender: string | null;
  default_recipient: string | null;
  retention_note: string | null;
  manage_permission: string | null;
  version: number | null;
  is_default: boolean;
  updated_at: string | null;
}

/** CustomerChannelOrderDto — a per-customer Zustellreihenfolge override. */
export interface CustomerChannelOrderDto {
  subject_type: 'person' | 'firma';
  subject_id: string;
  channel_order: ChannelKey[];
  version: number;
  updated_at: string | null;
}

/** ChannelMatrixEntryDto — one Kanal-Zuordnungsmatrix entry (docType × product × channel). */
export interface ChannelMatrixEntryDto {
  id: string;
  doc_type: DeliveryDocType;
  product: string | null;
  channel_key: ChannelKey;
  version: number;
  updated_at: string | null;
}

/**
 * ResolvedChannel — the result of ChannelResolutionService.resolveDeliveryChannel.
 * `fallback` = the resolved channel's configured fallbackChannel (nullable).
 */
export interface ResolvedChannel {
  channel: ChannelKey;
  fallback: ChannelKey | null;
  source: ChannelSource;
}
